import { graphql, useFragment, useMutation } from "react-relay";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogDescription,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { certificateEmitDialogFragment$key } from "./__generated__/certificateEmitDialogFragment.graphql";
import { useForm } from "@tanstack/react-form";
import { Spinner } from "@/components/ui/spinner";
import { Field, FieldLabel, FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import z from "zod";
import type { certificateEmitDialogMutation } from "./__generated__/certificateEmitDialogMutation.graphql";
import { toast } from "sonner";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";

export function CertificateEmitDialog({
	open,
	onClose,
	course,
}: {
	open: boolean;
	onClose: () => void;
	course: certificateEmitDialogFragment$key;
}) {
	const data = useFragment<certificateEmitDialogFragment$key>(
		graphql`
     fragment certificateEmitDialogFragment on Course {
       id
       name
     }
   `,
		course,
	);

	const { track } = useAnalytics();

	const [certificateEmit, _isPending] =
		useMutation<certificateEmitDialogMutation>(graphql`
    mutation certificateEmitDialogMutation($course: ID!, $name: String!) {
      certificateEmit(course: $course, name: $name) {
        id
        file {
          signedUrl
        }
      }
    }
  `);

	const form = useForm({
		defaultValues: {
			name: "",
		},
		validators: {
			onChange: z.object({
				name: z.string().min(3, "Nome deve ter pelo menos 3 caracteres"),
			}),
		},
		onSubmit: async ({ value }) => {
			return certificateEmit({
				variables: {
					course: data.id,
					name: value.name,
				},
				onCompleted: (response) => {
					track(AnalyticsEvents.COURSE_CERTIFICATE_EMITTED, {
						course_id: data.id,
					});

					fetch(response.certificateEmit.file.signedUrl)
						.then((response) => {
							if (!(response.status >= 200 && response.status < 300)) {
								toast.error(
									"Não foi possível gerar o certificado no momento. Tente novamente mais tarde ou entre em contato conosco.",
								);

								throw new Error("unable to create certificate");
							}

							return response.blob();
						})
						.then((blob) => {
							const blobUrl = window.URL.createObjectURL(blob);

							const link = document.createElement("a");
							// eslint-disable-next-line immutable/no-mutation
							link.href = blobUrl;
							link.setAttribute("download", "certificado.pdf");

							document.body.appendChild(link);

							link.click();

							link.remove();

							onClose();
						})
						.catch((_error) => console.error("Erro ao gerar PDF"));
				},
				onError: () => {
					toast.error(
						"Não foi possível gerar o certificado no momento. Tente novamente mais tarde ou entre em contato conosco.",
					);
				},
			});
		},
	});

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent>
				<form
					className="flex flex-col gap-6"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<DialogHeader>
						<DialogTitle>Emitir Certificado</DialogTitle>
					</DialogHeader>

					<DialogDescription>
						Parabéns por concluir o curso 🎉 🎉 🎉 Para gerar o certificado,
						preencha o campo abaixo com seu nome completo.{" "}
						<span className="text-red-600">
							Esse nome não poderá ser alterado para esse certificado depois
						</span>
					</DialogDescription>

					<form.Field name="name">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Nome Completo</FieldLabel>

								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Seu Nome"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<DialogFooter>
						<form.Subscribe selector={(state) => [state.isSubmitting]}>
							{([isSubmitting]) => (
								<>
									<Button
										variant="ghost"
										type="button"
										onClick={onClose}
										disabled={isSubmitting}
									>
										Cancelar
									</Button>

									<Button
										type="submit"
										disabled={isSubmitting}
										className="flex flex-row items-center"
									>
										Emitir {isSubmitting && <Spinner />}
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
